import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Wallet, Trophy, Car, ArrowRight } from "lucide-react";
import { formatUsd } from "../lib/currencyDisplay";
import { pageItems } from "../lib/pageItems";

export default function DashboardIndex() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const sections = [
      { label: "wallet", key: "wallet", pick: (r) => r.data },
      { label: "loyalty", key: "loyalty", pick: (r) => r.data },
      { label: "bookings", key: "bookings", pick: (r) => pageItems(r.data) },
    ];
    const results = await Promise.allSettled([
      axios.get(`${API}/wallet/me`, { withCredentials: true }),
      axios.get(`${API}/loyalty/me`, { withCredentials: true }),
      axios.get(`${API}/bookings/mine`, { withCredentials: true }),
    ]);
    const next = {};
    const failed = [];
    results.forEach((r, i) => {
      const sec = sections[i];
      if (r.status === "fulfilled") next[sec.key] = sec.pick(r.value);
      else failed.push(sec.label);
    });
    setStats(Object.keys(next).length ? next : null);
    setLoadErrors(failed);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.is_admin) { navigate("/dashboard/admin", { replace: true }); return; }
    load();
  }, [user, navigate, load]);

  if (!user) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="dashboard-index">
      <div className="eyebrow mb-2">Welcome back</div>
      <h1 className="zc-headline text-4xl md:text-5xl">{user.name?.split(" ")[0] || "Friend"}.</h1>
      <p className="text-[var(--zc-text-2)] mt-3">Rent, host, and invest from one account — no mode switching required.</p>

      {loading && (
        <p className="text-sm text-[var(--zc-text-3)] mt-4">Loading…</p>
      )}
      {loadErrors.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] text-sm flex flex-wrap items-center justify-between gap-2">
          <span className="text-[var(--zc-text-2)]">
            Some sections failed to load: {loadErrors.join(", ")}.
          </span>
          <button type="button" onClick={load} className="px-3 h-8 zc-btn-ghost text-xs font-semibold">
            Retry
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        <div className="zc-card p-6">
          <div className="flex items-center gap-2 eyebrow"><Wallet size={12} /> Wallet</div>
          <div className="zc-price text-3xl mt-3" data-testid="dash-wallet">${formatUsd(stats?.wallet?.wallet?.fiat_balance)}</div>
          <div className="text-xs text-[var(--zc-text-3)] mt-1">Available · Escrow ${formatUsd(stats?.wallet?.wallet?.escrow_balance)}</div>
        </div>
        <div className="zc-card p-6">
          <div className="flex items-center gap-2 eyebrow"><Trophy size={12} /> ZCT loyalty</div>
          <div className="zc-stat text-3xl mt-3" data-testid="dash-zct">{stats?.loyalty?.loyalty?.zct_balance || 0}</div>
          <div className="text-xs text-[var(--zc-text-3)] mt-1">Lifetime earned: {stats?.loyalty?.loyalty?.lifetime_earned || 0}</div>
        </div>
        <div className="zc-card p-6">
          <div className="flex items-center gap-2 eyebrow"><Car size={12} /> Trips</div>
          <div className="zc-stat text-3xl mt-3" data-testid="dash-trips">{stats?.bookings?.length || 0}</div>
          <div className="text-xs text-[var(--zc-text-3)] mt-1">Active and completed</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Link to="/dashboard/renter" className="zc-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="dash-go-renter">
          <div>
            <div className="eyebrow mb-1">Renter</div>
            <div className="zc-title text-2xl">My trips & wallet</div>
            <div className="text-sm text-[var(--zc-text-2)] mt-2">Upcoming trips, transaction history, ZCT redemption.</div>
          </div>
          <ArrowRight size={18} />
        </Link>
        <Link to="/host?tab=cars" className="zc-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="dash-go-owner">
          <div>
            <div className="eyebrow mb-1">Host hub · Cars</div>
            <div className="zc-title text-2xl">My fleet & earnings</div>
            <div className="text-sm text-[var(--zc-text-2)] mt-2">List vehicles, manage bookings, banking for payouts.</div>
          </div>
          <ArrowRight size={18} />
        </Link>
        <Link to="/host?tab=parking" className="zc-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="dash-go-parking">
          <div>
            <div className="eyebrow mb-1">Host hub · Parking</div>
            <div className="zc-title text-2xl">My parking spots</div>
            <div className="text-sm text-[var(--zc-text-2)] mt-2">List driveways, garages, lots — same payout account.</div>
          </div>
          <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}
